import React, { useEffect, useId, useMemo, useState } from 'react';
import { animate, useMotionValue, useMotionValueEvent } from 'framer-motion';
import { CalendarCheck2, HandCoins, UserRoundSearch } from 'lucide-react';
import styles from './GrowthCalculator.module.css';
import Button from '../Button/Button';

const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;

/** Number that counts to its new value */
function Counter({ value, className }) {
    const mv = useMotionValue(value);
    const [shown, setShown] = useState(value);
    useMotionValueEvent(mv, 'change', (v) => setShown(v));
    useEffect(() => {
        const controls = animate(mv, value, { duration: 0.6, ease: [0.22, 1, 0.36, 1] });
        return () => controls.stop();
    }, [mv, value]);
    return <span className={className}>{inr(shown)}</span>;
}

function Slider({ label, value, min, max, step, onChange, format }) {
    const id = useId();
    const pct = ((value - min) / (max - min)) * 100;
    return (
        <div className={styles.slider}>
            <label htmlFor={id}><span>{label}</span><b>{format(value)}</b></label>
            <input id={id} type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} style={{ '--pct': `${pct}%` }} />
        </div>
    );
}

export default function GrowthCalculator() {
    const [lapsed, setLapsed] = useState(300);
    const [comeback, setComeback] = useState(10);
    const [avgBill, setAvgBill] = useState(1000);
    const [noShows, setNoShows] = useState(5);
    const [cards, setCards] = useState(10);
    const [cardValue, setCardValue] = useState(5000);

    const parts = useMemo(() => {
        const winBack = lapsed * (comeback / 100) * avgBill;
        const fewerNoShows = noShows * avgBill * 4;
        const advance = cards * cardValue;
        return { winBack, fewerNoShows, advance, total: winBack + fewerNoShows + advance };
    }, [lapsed, comeback, avgBill, noShows, cards, cardValue]);

    return (
        <div className={styles.wrap}>
            <div className={styles.inputs}>
                <Slider label="Clients who have not visited in 60+ days" value={lapsed} min={0} max={1500} step={10} onChange={setLapsed} format={(v) => v} />
                <Slider label="Of them, how many come back after a WhatsApp offer" value={comeback} min={0} max={30} step={1} onChange={setComeback} format={(v) => `${v}%`} />
                <Slider label="Your average bill" value={avgBill} min={300} max={5000} step={50} onChange={setAvgBill} format={inr} />
                <Slider label="Fewer no-shows a week, thanks to reminders" value={noShows} min={0} max={30} step={1} onChange={setNoShows} format={(v) => v} />
                <Slider label="Prepaid cards sold a month" value={cards} min={0} max={60} step={1} onChange={setCards} format={(v) => v} />
                <Slider label="Value of one prepaid card" value={cardValue} min={1000} max={25000} step={500} onChange={setCardValue} format={inr} />
            </div>

            <div className={styles.result} aria-live="polite">
                <span className={styles.resultLabel}>Extra sales and advance cash, every month</span>
                <Counter value={parts.total} className={styles.total} />
                <span className={styles.year}>About <Counter value={parts.total * 12} /> a year</span>

                <ul className={styles.parts}>
                    <li><UserRoundSearch size={15} /> <span>Lost clients won back</span> <Counter value={parts.winBack} /></li>
                    <li><CalendarCheck2 size={15} /> <span>Fewer empty chairs</span> <Counter value={parts.fewerNoShows} /></li>
                    <li><HandCoins size={15} /> <span>Collected in advance</span> <Counter value={parts.advance} /></li>
                </ul>

                <Button to="/demo" variant="light" size="lg">See it on your salon data</Button>
                <p className={styles.note}>An estimate from the numbers you entered, not a promise. Every salon is different.</p>
            </div>
        </div>
    );
}
